import React, { useEffect, useState } from 'react';
import Modal from '../../../components/common/modal';
import { useSystemMessages } from '../../../hooks/useComms';
import { useSendOverallFeedback } from '../../../hooks/useUsers';

const FEEDBACK_GROUP_KEY = 'listener_message_rejected';

interface SendOverallFeedbackModalProps {
  userId: number;
  listenerEmail: string;
  onClose: () => void;
}

const SendOverallFeedbackModal: React.FC<SendOverallFeedbackModalProps> = ({ userId, listenerEmail, onClose }) => {
  const { data: feedbackTemplates = [], isLoading: isLoadingTemplates } = useSystemMessages({
    only_group_key: FEEDBACK_GROUP_KEY,
  });
  const { mutate: sendFeedback, isLoading: isSending } = useSendOverallFeedback();

  const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(null);
  const [subject, setSubject] = useState<string>('');
  const [message, setMessage] = useState<string>('');

  useEffect(() => {
    if (feedbackTemplates.length > 0 && selectedTemplateId === null) {
      setSelectedTemplateId(feedbackTemplates[0].id);
      setSubject(feedbackTemplates[0].subject ?? '');
      setMessage(feedbackTemplates[0].message ?? '');
    }
  }, [feedbackTemplates, selectedTemplateId]);

  const handleTemplateChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const val = e.target.value;
    if (val === '') {
      setSelectedTemplateId(null);
      setSubject('');
      setMessage('');
      return;
    }
    const id = Number(val);
    const template = feedbackTemplates.find((t: SystemMessage) => t.id === id);
    setSelectedTemplateId(id);
    setSubject(template?.subject ?? '');
    setMessage(template?.message ?? '');
  };

  const handleSend = () => {
    if (!selectedTemplateId || !userId) return;
    sendFeedback({ templateId: selectedTemplateId, userId, subject, message }, { onSuccess: onClose });
  };

  return (
    <Modal
      isModel={true}
      InnerComponent={
        <div className="relative w-full max-w-lg rounded-lg bg-white shadow-xl" onClick={(e) => e.stopPropagation()}>
          <div className="max-h-[80vh] overflow-y-auto p-6">
            <h3 className="mb-4 text-lg font-bold text-gray-900">Send Overall Feedback</h3>

            <div className="mb-4">
              <label className="mb-1 block text-sm font-medium text-gray-700">Template</label>
              {isLoadingTemplates ? (
                <p className="text-sm text-gray-400">Loading templates...</p>
              ) : feedbackTemplates.length === 0 ? (
                <div className="rounded border border-yellow-200 bg-yellow-50 px-3 py-2 text-sm text-yellow-800">
                  No templates found in group <strong>{FEEDBACK_GROUP_KEY}</strong>. Make sure messages exist in{' '}
                  <em>Communications &gt; Campaign messages</em> under that group.
                </div>
              ) : (
                <select
                  className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={selectedTemplateId ?? ''}
                  onChange={handleTemplateChange}
                >
                  <option value="">— Select a template —</option>
                  {feedbackTemplates.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name}
                    </option>
                  ))}
                </select>
              )}
            </div>

            <div className="mb-4">
              <label className="mb-1 block text-sm font-medium text-gray-700">Subject</label>
              <input
                type="text"
                className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Email subject..."
                disabled={feedbackTemplates.length === 0}
              />
            </div>

            <div className="mb-6">
              <label className="mb-1 block text-sm font-medium text-gray-700">Message</label>
              <textarea
                className="h-40 w-full rounded border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Message to send to the listener..."
                disabled={feedbackTemplates.length === 0}
              />
              <p className="mt-1 text-xs text-gray-400">
                Sending to: <span className="font-medium text-gray-600">{listenerEmail}</span>
              </p>
            </div>

            <div className="flex justify-end gap-3 border-t border-gray-100 pt-4">
              <button
                className="rounded border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                onClick={onClose}
                disabled={isSending}
              >
                Cancel
              </button>
              <button
                className="bg-blue-primary rounded px-4 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50"
                onClick={handleSend}
                disabled={isSending || !selectedTemplateId || !userId}
              >
                {isSending ? 'Sending...' : 'Send'}
              </button>
            </div>
          </div>
        </div>
      }
    />
  );
};

export default SendOverallFeedbackModal;
